import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  node_imageBaseUrl,
  formatDateToDDMMYYYY,
  imageCustomOrderUrl,
  imageBaseUrl,
  getUserData,
} from "./const";

import statusColors from "../utils/statusColors";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { createErpSocket } from "../utils/socketIo";
import { listOrderQueue, postOrderQueue } from "../erpClient";
import { parseApiPagination } from "../utils/parseApiPagination";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";
import { buildOrderQueueNavigationState } from "../utils/queueNavigation";

// Utility used in multiple places to check "empty" numeric/id values
const isZeroOrNullValue = (value) =>
  value === 0 ||
  value === "0" ||
  value === null ||
  value === undefined ||
  value === "";

// Build product image URL from API path (handles various field names and path formats)
const getProductImageUrl = (rawPath) => {
  if (!rawPath || typeof rawPath !== "string") return null;
  const p = rawPath.trim();
  if (
    p.startsWith("http://") ||
    p.startsWith("https://") ||
    p.startsWith("data:")
  )
    return p;
  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  return `${base}/${p.replace(/^\/+/, "")}`;
};

// Get product image path from any of the common API field names
const getProductImagePath = (prod) =>
  prod?.design_photos ||
  prod?.design_photo ||
  prod?.image ||
  prod?.image_path ||
  prod?.photo ||
  null;

// CSS styles for row colors - FULL BACKGROUND COLOR ONLY with 2% opacity
const rowStyles = `
  .readypro-row {
    background-color: rgba(255, 165, 0, 0.02) !important;
    color: #000 !important;
  }
  .readypro-row td {
    background-color: rgba(255, 165, 0, 0.02) !important;
    color: #000 !important;
  }
  .copro-row {
    background-color: rgba(0, 128, 255, 0.02) !important;
    color: #000 !important;
  }
  .copro-row td {
    background-color: rgba(0, 128, 255, 0.02) !important;
    color: #000 !important;
  }
  .repair-row {
    background-color: rgba(255, 0, 0, 0.02) !important;
    color: #000 !important;
  }
  .repair-row td {
    background-color: rgba(255, 0, 0, 0.02) !important;
    color: #000 !important;
  }
  
  /* Badge styles for better text visibility */
  .badge-readypro {
    background-color: #ff9800 !important;
    color: #fff !important;
    font-weight: bold !important;
  }
  .badge-copro {
    background-color: #2196f3 !important;
    color: #fff !important;
    font-weight: bold !important;
  }
  .badge-repair {
    background-color: #f44336 !important;
    color: #fff !important;
    font-weight: bold !important;
  }
`;

// Inject CSS styles with unique ID to avoid conflicts
if (typeof document !== "undefined") {
  // Remove existing stylesheet if it exists
  const existingStyle = document.getElementById("order-queue-row-styles");
  if (existingStyle) {
    existingStyle.remove();
  }

  const styleSheet = document.createElement("style");
  styleSheet.id = "order-queue-row-styles";
  styleSheet.type = "text/css";
  styleSheet.innerText = rowStyles;
  document.head.appendChild(styleSheet);
}

function OrderQueue() {
  const { buttonClass, cardClass, tableClass, buttonTableStyle } =
    useButtonTableStyle();
  const [orders, setOrders] = useState([]);
  const [locks, setLocks] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [selectedOrderForProductModal, setSelectedOrderForProductModal] =
    useState(null);
  const [socket, setSocket] = useState(null);
  const navigate = useNavigate();

  // Prevent duplicate concurrent fetches
  const isFetching = useRef(false);

  // Lock mechanism refs and state
  const heldLocksRef = useRef(new Set());
  const currentUserId = React.useMemo(() => {
    try {
      const data = getUserData ? getUserData() : null;
      return (
        data?.id ||
        data?.user_id ||
        data?.userId ||
        data?.userid ||
        data?.userID ||
        null
      );
    } catch (err) {
      console.error("Unable to read current user", err);
      return null;
    }
  }, []);

  // const lockApiUrl = "https://api-syn.vedascodetech.in/api/order_queue";

  // const lockApiHeaders = {
  //   Authorization:
  //     "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoxLCJyb2xlIjoiQURNSU4iLCJpYXQiOjE3NjY5ODIyNDgsImV4cCI6MTc2Nzg0NjI0OH0.vKhw6HUx_EIhQLPexsXN-kiKyQrBXML5Ei3B_3bGGvM",
  //   "Content-Type": "application/json",
  // };

  // Lock mechanism utility functions
  const persistHeldLocks = (locksSet) => {
    const ids = Array.from(locksSet || []);
    try {
      sessionStorage.setItem("order_queue_locks", JSON.stringify(ids));
    } catch {
      // ignore storage failures
    }
  };

  const restoreAndReleaseOwnLocks = async () => {
    if (!currentUserId) return;
    try {
      const stored =
        JSON.parse(sessionStorage.getItem("order_queue_locks") || "[]") || [];
      if (stored.length > 0) {
        await postOrderQueue({
          type: "unlock",
          order_ids: stored,
          user_id: currentUserId,
        });
        sessionStorage.removeItem("order_queue_locks");
      }
    } catch (err) {
      console.warn("Failed to release stored locks on load", err);
    }
  };

  // Make sure stale locks from the same user are cleared when they revisit/refresh
  useEffect(() => {
    restoreAndReleaseOwnLocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Listen for API URL changes (OrderQueue will refresh via parent component)
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log("API URL changed in OrderQueue...", event.detail);
      // Orders will be refetched when parent component refreshes
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const applyLockState = (orderId, lock) => {
    setLocks((prev) => {
      const next = { ...prev };
      if (lock) {
        next[orderId] = lock;
      } else {
        delete next[orderId];
      }
      return next;
    });
  };

  const fetchLockStatus = async (orderIds) => {
    const ids = orderIds?.length ? orderIds : orders.map((o) => o.id);
    if (!ids || ids.length === 0) return;
    try {
      const res = await postOrderQueue({
        type: "status",
        order_ids: ids,
      });
      const payload = res?.data?.locks || res?.locks || res?.data || {};
      setLocks(payload || {});
    } catch (err) {
      console.warn("Unable to fetch lock status", err);
    }
  };

  const attemptLock = async (orderId) => {
    if (!orderId || !currentUserId) return true; // allow navigation if user context missing
    try {
      const res = await postOrderQueue({
        type: "lock",
        order_id: orderId,
        user_id: currentUserId,
      });
      const newLock = res?.data?.lock ||
        res?.data ||
        res?.lock || { user_id: currentUserId, locked_at: Date.now() };
      applyLockState(orderId, newLock);
      heldLocksRef.current.add(orderId);
      persistHeldLocks(heldLocksRef.current);
      return true;
    } catch (err) {
      const lockedByOther = err?.response?.status === 423;
      if (lockedByOther) {
        const lock =
          err.response?.data?.data?.lock || err.response?.data?.data || null;
        if (lock) {
          applyLockState(orderId, lock);
        }
        toast.error("Order is already being processed by another user");
      } else {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            "Unable to lock this order",
        );
      }
      return false;
    }
  };

  const releaseLocksForOrders = async (orderIds) => {
    if (!orderIds || orderIds.length === 0 || !currentUserId) return;
    try {
      await postOrderQueue({
        type: "unlock",
        order_ids: orderIds,
        user_id: currentUserId,
      });
      orderIds.forEach((id) => applyLockState(id, null));
    } catch (err) {
      console.warn("Failed to release locks", err);
    }
  };

  const handleLockedNavigation = async (order, path, state) => {
    const locked = await attemptLock(order.id);
    if (!locked) return;
    heldLocksRef.current.add(order.id);
    persistHeldLocks(heldLocksRef.current);
    navigate(path, { state });
  };

  const pageCache = useVisitedPageCache();

  const fetchOrders = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const cacheKey = visitedPageKey([page, limit, search.trim()]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        setOrders(cached.items);
        setTotalCount(cached.total);
        setIsLoading(false);
        return;
      }
      if (isFetching.current) return;
      isFetching.current = true;
      setIsLoading(true);

      try {
        const response = await listOrderQueue({
          search: search.trim(),
          page,
          limit,
        });

        let ordersData = [];
        if (response?.data && Array.isArray(response.data)) {
          ordersData = response.data;
        } else if (Array.isArray(response?.data?.data)) {
          ordersData = response.data.data;
        } else if (Array.isArray(response?.data?.items)) {
          ordersData = response.data.items;
        } else if (Array.isArray(response)) {
          ordersData = response;
        }

        const total = parseApiPagination(response, ordersData.length).total;
        setOrders(ordersData);
        setTotalCount(total);
        pageCache.write(cacheKey, { items: ordersData, total });
      } catch (error) {
        console.error("Order queue fetch failed:", error);
        setOrders([]);
        setTotalCount(0);
        toast.error(
          error.response?.data?.message || "Failed to fetch order queue",
        );
      } finally {
        setIsLoading(false);
        isFetching.current = false;
      }
    },
    [],
  );

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchOrders(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchOrders]);

  useEffect(() => {
    fetchLockStatus();
  }, []);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("OrderQueue: Connected to socket server");
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("OrderQueue: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("OrderQueue: Socket connection error:", error);
    });

    newSocket.on("order_queue_created", async (data) => {
      // console.log("🔔 Socket event received:", data);

      if (!data?.order_queue_data || !Array.isArray(data.order_queue_data))
        return;

      const newOrderData = data.order_queue_data;

      // Process the order queue data to match the existing orders format
      const orderGroups = {};

      // Group by oqd_id (order queue details ID)
      newOrderData.forEach((row) => {
        const oqdId = row.oqd_id;
        if (!orderGroups[oqdId]) {
          orderGroups[oqdId] = {
            id: oqdId,
            // by_user: row.by_user,
            customer_form_id: row.customer_form_id,
            customer_user_id: row.customer_user_id,
            token_number: row.token_number, // Add token_number
            name: row.user_name, // Add name from user_name
            mobile: row.user_mobile, // Add mobile from user_mobile
            area: row.user_area, // Add area from user_area
            page_source: row.page_source,
            sale_id: row.sale_id,
            custom_order_id: row.custom_order_id,
            repairing_id: row.repairing_id,
            status: row.status,
            created_at: row.created_at,
            products: [],
          };
        }

        // Add product data
        if (row.oqp_id) {
          orderGroups[oqdId].products.push({
            id: row.oqp_id,
            oqdetails_id: row.oqdetails_id,
            pro_id: row.pro_id,
            design: row.pro_design,
            tag_no: row.pro_tag_no,
            pro_size: row.pro_size,
            pcs: row.pro_pcs,
            gwt: row.pro_gwt,
            lwt: row.pro_lwt,
            nwt: row.pro_nwt,
            product_group: row.product_group,
            product_group_name: row.product_group_name,
            description: row.description,
            design_photos: row.design_photos,
            size: row.size,
            expected_weight: row.expected_weight,
            current_weight: row.current_weight,
            price_type: row.price_type,
            price_value: row.price_value,
            expected_price: row.expected_price,
            delivery_date: row.delivery_date,
            created_at: row.created_at,
          });
        }
      });

      // Convert to array
      const newOrders = Object.values(orderGroups);
      // console.log("🔄 Processed socket orders:", newOrders);

      // Small delay to ensure state is updated
      setTimeout(() => {
        setOrders((prev) => {
          // Use a map for easy upsert/delete by id
          const map = new Map(prev.map((order) => [String(order.id), order]));

          newOrders.forEach((order) => {
            const idKey = String(order.id);
            const isCompleted =
              !isZeroOrNullValue(order.sale_id) ||
              !isZeroOrNullValue(order.custom_order_id) ||
              !isZeroOrNullValue(order.repairing_id);

            if (isCompleted) {
              // If this order is now converted to sale/custom/repair, remove from queue
              map.delete(idKey);
            } else {
              // Upsert fresh/active queue entry
              const existing = map.get(idKey) || {};
              map.set(idKey, { ...existing, ...order });
            }
          });

          const updated = Array.from(map.values()).sort((a, b) => {
            if (a.created_at && b.created_at) {
              return new Date(b.created_at) - new Date(a.created_at);
            }
            return Number(b.id) - Number(a.id);
          });

          // console.log("✅ Updated orders state:", updated);
          return updated;
        });
      }, 100);
    });

    newSocket.on("order_queue_lock", (data) => {
      if (!data?.order_id) return;
      applyLockState(data.order_id, {
        user_id: data.user_id,
        locked_at: data.locked_at,
      });
    });

    newSocket.on("order_queue_unlock", (data) => {
      if (!data?.order_id) return;
      applyLockState(data.order_id, null);
    });

    return () => {
      newSocket.off("order_queue_created");
      newSocket.off("order_queue_lock");
      newSocket.off("order_queue_unlock");
      newSocket.disconnect();
    };
  }, []);

  // Sort orders by id (or created_at) descending for most recent first
  const sortedOrders = [...orders].sort((a, b) => {
    if (a.created_at && b.created_at) {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return Number(b.id) - Number(a.id);
  });

  // Client filters on current server page (type + unassigned)
  const filteredOrders = sortedOrders.filter((order) => {
    const matchesType =
      typeFilter === "" ||
      String(order.page_source).toUpperCase() ===
        String(typeFilter).toUpperCase();

    const isUnassigned =
      isZeroOrNullValue(order.sale_id) &&
      isZeroOrNullValue(order.custom_order_id) &&
      isZeroOrNullValue(order.repairing_id);

    return matchesType && isUnassigned;
  });

  const availableTypes = React.useMemo(() => {
    const typeSet = new Set();
    orders.forEach((order) => {
      if (order.page_source) {
        typeSet.add(String(order.page_source).toUpperCase());
      }
    });
    return Array.from(typeSet);
  }, [orders]);

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const displayedOrders = filteredOrders;

  // Refresh lock state whenever the visible list changes
  useEffect(() => {
    if (displayedOrders.length > 0) {
      fetchLockStatus(displayedOrders.map((o) => o.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedOrders.length, currentPage]);

  // Ensure locks held by this user are released if the browser/tab is closed
  useEffect(() => {
    const handleBeforeUnload = () => {
      const held = Array.from(heldLocksRef.current);
      if (held.length > 0) {
        releaseLocksForOrders(held);
        heldLocksRef.current.clear();
        sessionStorage.removeItem("order_queue_locks");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // View modal logic
  const handleViewClick = (order) => {
    setSelectedOrder(order);
    setShowViewModal(true);
  };

  // Handle Move to Sales button with lock mechanism
  const handleMoveToSales = async (order) => {
    await handleLockedNavigation(
      order,
      "/sales",
      buildOrderQueueNavigationState(order, "READYPRO"),
    );
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Order Queue</h4>
            {/* Add button can be implemented if needed */}
          </div>

          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${
                  buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                className={`form-select w-auto ms-3 ${
                  buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
                style={{ maxWidth: "200px" }}
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Types</option>
                {availableTypes.map((typeValue) => (
                  <option key={typeValue} value={typeValue}>
                    {typeValue}
                  </option>
                ))}
              </select>
            </div>

            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Token</th>
                    <th>Customer</th>
                    <th>Mobile</th>
                    <th>Product</th>
                    <th>Type</th>
                    <th>Items</th>

                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedOrders.length > 0 ? (
                    displayedOrders.map((order) => {
                      // Customer data is now directly available in the order object
                      const customerName = order.name || "N/A";
                      const customerMobile = order.mobile || "N/A";
                      const tokenNumber = order.token_number || `#${order.id}`;

                      // Check lock status
                      const orderLock = locks[order.id];
                      const lockedByOther =
                        orderLock &&
                        currentUserId &&
                        String(orderLock.user_id) !== String(currentUserId);

                      const firstProduct =
                        order.products && order.products[0]
                          ? order.products[0]
                          : {};

                      // Simplified product display logic using product_group_name from API
                      let productDisplayName = "N/A";
                      if (firstProduct.product_group_name) {
                        productDisplayName = firstProduct.product_group_name;

                        // Add additional info based on page_source
                        if (
                          order.page_source === "COPRO" &&
                          firstProduct.description
                        ) {
                          productDisplayName += ` - ${firstProduct.description}`;
                        } else if (
                          order.page_source === "REPAIR" &&
                          firstProduct.description
                        ) {
                          const issue = firstProduct.description.substring(
                            0,
                            30,
                          );
                          productDisplayName += ` - ${issue}${
                            firstProduct.description.length > 30 ? "..." : ""
                          }`;
                        }
                      } else if (firstProduct.product_group) {
                        productDisplayName = `Group ${firstProduct.product_group}`;
                      }

                      // Set row class based on page_source
                      let rowClassName = "";
                      if (order.page_source === "READYPRO") {
                        rowClassName = "readypro-row";
                      } else if (order.page_source === "COPRO") {
                        rowClassName = "copro-row";
                      } else if (order.page_source === "REPAIR") {
                        rowClassName = "repair-row";
                      }

                      return (
                        <tr key={order.id} className={rowClassName}>
                          <td>
                            <span
                              className={`badge ${statusColors.secondary}`}
                              style={{ fontSize: "1em" }}
                            >
                              {tokenNumber}
                            </span>
                          </td>
                          <td>{customerName}</td>
                          <td>{customerMobile}</td>
                          <td>
                            {productDisplayName}
                            {/* Removed i button for product details */}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                order.page_source === "READYPRO"
                                  ? "badge-readypro"
                                  : order.page_source === "REPAIR"
                                    ? "badge-repair"
                                    : "badge-copro"
                              }`}
                              style={{
                                fontSize: "1em",
                                textTransform: "uppercase",
                              }}
                            >
                              {order.page_source}
                            </span>
                          </td>
                          <td>{order.products ? order.products.length : 0}</td>

                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className={`btn btn-primary btn-sm ${buttonClass}`}
                                onClick={() => {
                                  setSelectedProductDetails(order.products);
                                  setSelectedOrderForProductModal(order);
                                  setShowProductModal(true);
                                }}
                                title="View Product Details"
                              >
                                <Icon
                                  icon="solar:eye-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>

                              {order.page_source === "COPRO" ? (
                                <button
                                  className={`btn btn-secondary btn-sm ${buttonClass}`}
                                  disabled={lockedByOther}
                                  onClick={async () => {
                                    await handleLockedNavigation(
                                      order,
                                      "/customorderentry",
                                      buildOrderQueueNavigationState(order, "COPRO"),
                                    );
                                  }}
                                  title={
                                    lockedByOther
                                      ? "Locked by another user"
                                      : "Move to Custom Order Entry"
                                  }
                                >
                                  <Icon
                                    icon="solar:document-add-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                              ) : order.page_source === "READYPRO" ? (
                                <button
                                  className={`btn btn-warning btn-sm ${buttonClass}`}
                                  disabled={lockedByOther}
                                  onClick={() => handleMoveToSales(order)}
                                  title={
                                    lockedByOther
                                      ? "Locked by another user"
                                      : "Move to Sales Entry"
                                  }
                                >
                                  <Icon
                                    icon="solar:cart-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                              ) : order.page_source === "REPAIR" ? (
                                <button
                                  className={`btn btn-info btn-sm ${buttonClass}`}
                                  disabled={lockedByOther}
                                  onClick={async () => {
                                    // console.log(
                                    //   "🚀 OrderQueue - Sending to RepairingOrder:",
                                    //   {
                                    //     customOrderId: order.id,
                                    //     customerUserId: order.customer_user_id,
                                    //   }
                                    // );

                                    await handleLockedNavigation(
                                      order,
                                      "/repairingorderentry",
                                      buildOrderQueueNavigationState(order, "REPAIR"),
                                    );
                                  }}
                                  title={
                                    lockedByOther
                                      ? "Locked by another user"
                                      : "Move to Repairing Order Entry"
                                  }
                                >
                                  <Icon
                                    icon="solar:settings-minimalistic-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* View Order Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
        size="lg"
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Header closeButton>
          <Modal.Title>Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder ? (
            <div>
              <div>
                <strong>Order No:</strong>{" "}
                {selectedOrder.order_number || selectedOrder.id}
              </div>
              <div>
                <strong>Customer Name:</strong> {selectedOrder.name || "N/A"}
              </div>
              <div>
                <strong>Status:</strong>{" "}
                {selectedOrder.status === "1" ? "Active" : "Inactive"}
              </div>
              {/* Add more fields as needed */}
            </div>
          ) : (
            <div>No details available.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className={buttonClass}
            onClick={() => setShowViewModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Product Details Modal */}
      <Modal
        show={showProductModal}
        onHide={() => setShowProductModal(false)}
        centered
        size="md"
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Header closeButton>
          <div className="d-flex w-100 align-items-center justify-content-between">
            <Modal.Title className="mb-0">Product Details</Modal.Title>
            {/* Token Number Badge */}
            {selectedOrderForProductModal && (
              <span
                className={`badge ${statusColors.secondary}`}
                style={{ fontSize: "1em" }}
              >
                {selectedOrderForProductModal.token_number ||
                  `#${selectedOrderForProductModal.id}` ||
                  "N/A"}
              </span>
            )}
            {/* End Token Number Badge */}
          </div>
        </Modal.Header>
        {/* Customer Name just below header */}
        {selectedOrderForProductModal && (
          <div
            className="ms-4 mt-2 mb-0"
            style={{
              fontSize: "1.08em",
              fontWeight: 500,
              textAlign: "left",
              color: "#212529",
            }}
          >
            Name: {selectedOrderForProductModal.name || "N/A"}
          </div>
        )}
        {/* End Customer Name */}
        <Modal.Body>
          {selectedProductDetails && selectedProductDetails.length > 0 ? (
            <div>
              {selectedOrderForProductModal &&
              selectedOrderForProductModal.page_source === "READYPRO" ? (
                // READYPRO custom popup
                <div>
                  {selectedProductDetails.map((prod, idx) => {
                    // Use data directly from the API response
                    const design = prod.design || "N/A";
                    const tagNo = prod.tag_no || "N/A";
                    const size = prod.pro_size || prod.size || "N/A";
                    const gwt = prod.gwt || "N/A";
                    const lwt = prod.lwt || "N/A";
                    const nwt = prod.nwt || "N/A";
                    const pcs = prod.pcs || "N/A";
                    const groupName = prod.product_group_name || "N/A";
                    const imagePath = getProductImagePath(prod);
                    const imageUrl = getProductImageUrl(imagePath);

                    return (
                      <div
                        key={idx}
                        className="mb-3 p-2 border rounded d-flex flex-column flex-md-row align-items-center"
                        style={{
                          color: "#212529",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          className="w-100 d-flex justify-content-center align-items-center mb-2 mb-md-0 me-md-3"
                          style={{
                            minWidth: "120px",
                            maxWidth: "120px",
                            minHeight: "120px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "6px",
                            border: "1px solid #eee",
                          }}
                        >
                          {imagePath ? (
                            <img
                              src={`${node_imageBaseUrl}/${imagePath}`}
                              alt="Product"
                              style={{
                                maxWidth: "120px",
                                maxHeight: "120px",
                                objectFit: "contain",
                                borderRadius: "6px",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                const placeholder = e.target.nextElementSibling;
                                if (placeholder)
                                  placeholder.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <span
                            className="text-muted small text-center"
                            style={{
                              display: imageUrl ? "none" : "flex",
                              padding: "8px",
                            }}
                          >
                            No image
                          </span>
                        </div>

                        <div
                          style={{ minWidth: 0, flex: 1, textAlign: "left" }}
                        >
                          <div>
                            <strong>Design:</strong> {design}
                          </div>
                          <div>
                            <strong>Product Group:</strong> {groupName}
                          </div>
                          <div>
                            <strong>Tag No:</strong> {tagNo}
                          </div>
                          <div>
                            <strong>Size:</strong> {size}
                          </div>
                          <div>
                            <strong>GWT:</strong> {gwt}
                          </div>
                          <div>
                            <strong>LWT:</strong> {lwt}
                          </div>
                          <div>
                            <strong>NWT:</strong> {nwt}
                          </div>
                          <div>
                            <strong>Pcs:</strong> {pcs}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // COPRO/REPAIR popup
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  {selectedProductDetails.map((prod, idx) => {
                    const imagePath = getProductImagePath(prod);
                    const imageUrl = getProductImageUrl(imagePath);
                    return (
                      <div
                        key={idx}
                        className="mb-3 p-2 border rounded d-flex flex-column flex-md-row align-items-center"
                        style={{
                          color: "#212529",
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Always render image container for consistent layout */}
                        <div
                          className="w-100 d-flex justify-content-center align-items-center mb-2 mb-md-0 me-md-3"
                          style={{
                            minWidth: "120px",
                            maxWidth: "120px",
                            minHeight: "120px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "6px",
                            border: "1px solid #eee",
                          }}
                        >
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt="Product Design"
                              style={{
                                maxWidth: "120px",
                                maxHeight: "120px",
                                objectFit: "contain",
                                borderRadius: "6px",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                const placeholder = e.target.nextElementSibling;
                                if (placeholder)
                                  placeholder.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <span
                            className="text-muted small text-center"
                            style={{
                              display: imageUrl ? "none" : "flex",
                              padding: "8px",
                            }}
                          >
                            No image
                          </span>
                        </div>
                        <div
                          style={{ minWidth: 0, flex: 1, textAlign: "left" }}
                        >
                          <div>
                            <strong>Product Group:</strong>{" "}
                            {prod.product_group_name ||
                              prod.product_group ||
                              "N/A"}
                          </div>
                          {prod.description && (
                            <div>
                              <strong>Description:</strong> {prod.description}
                            </div>
                          )}
                          {prod.size && (
                            <div>
                              <strong>Size:</strong> {prod.size}
                            </div>
                          )}
                          {prod.expected_weight && (
                            <div>
                              <strong>Est Weight:</strong>{" "}
                              {prod.expected_weight}
                            </div>
                          )}
                          {prod.expected_price && (
                            <div>
                              <strong>Est Price:</strong> ₹{prod.expected_price}
                            </div>
                          )}
                          {prod.delivery_date && (
                            <div>
                              <strong>Delivery Date:</strong>{" "}
                              {formatDateToDDMMYYYY(prod.delivery_date)}
                            </div>
                          )}
                          {prod.current_weight && (
                            <div>
                              <strong>Current Weight:</strong>{" "}
                              {prod.current_weight}
                            </div>
                          )}
                          {prod.price_type && (
                            <div>
                              <strong>Price Type:</strong> {prod.price_type} (
                              {prod.price_value || "N/A"})
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>No product details available.</div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default OrderQueue;
